import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { PlayerAvatar } from "@/components/game/player-avatar";
import { cn } from "@/lib/utils";

export interface EnhancedPlayerAvatarProps {
  player: any;
  showControls?: boolean;
  isOpponent?: boolean;
  isConnected: boolean;
  currentTurn: number;
  scores: { [key: number]: number };
  playerCardCounts: { [key: number]: number };
  cardsDealt: boolean;
  isRoundEnded: boolean;
  gameSettings?: {
    timer: {
      timerEnabled: boolean;
      timerDuration: number;
      timerWarning: number;
    };
  };
}

export const EnhancedPlayerAvatar: React.FC<EnhancedPlayerAvatarProps> = ({
  player,
  showControls = false,
  isOpponent = false,
  isConnected,
  currentTurn,
  scores,
  playerCardCounts,
  cardsDealt,
  isRoundEnded,
  gameSettings,
}) => {
  const isPlayerTurn = currentTurn === player.id;
  const playerScore = scores[player.id] || 0;
  const playerCardCount = playerCardCounts[player.id] || 0;

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const [timeLeft, setTimeLeft] = useState(gameSettings?.timer.timerDuration || 30);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (gameSettings?.timer.timerEnabled && isPlayerTurn && !isRoundEnded) {
      setTimeLeft(gameSettings.timer.timerDuration);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [isPlayerTurn, isRoundEnded, gameSettings?.timer]);

  return (
    <div className="flex flex-col items-center gap-3 relative">
      <motion.div
        className={cn(
          "flex items-center gap-4 p-3 rounded-lg",
          "transition-all duration-300",
          isOpponent ? "flex-row-reverse" : "flex-row",
        )}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
      >
        <div className="relative">
          <motion.div
            className="relative"
            initial={false}
            animate={
              isPlayerTurn
                ? {
                    scale: [1, 1.1, 1],
                  }
                : {}
            }
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div
              className={cn(
                "absolute -inset-1 rounded-full opacity-50",
                isPlayerTurn
                  ? "bg-gradient-to-br from-yellow-400/30 to-yellow-600/20 animate-pulse"
                  : "hidden",
              )}
            ></div>
            <div className={cn("transition-all duration-300 relative z-10")}> 
              <PlayerAvatar 
                player={player} 
                isActive={isPlayerTurn}
                isConnected={isConnected}
                isOpponent={isOpponent}
                size="lg"
              />
            </div>
          </motion.div>
        </div>
        <motion.div
          className="flex flex-col"
          initial={{ opacity: 0, x: isOpponent ? 10 : -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "font-medium",
                isPlayerTurn ? "theme-text-primary" : "text-white",
              )}
            >
              {player.username}
            </span>
            {isPlayerTurn && cardsDealt && !isRoundEnded && (
              <div className="flex items-center gap-2">
                {gameSettings?.timer.timerEnabled && cardsDealt && !isRoundEnded && (
                  <Badge
                    variant="destructive"
                    className={cn(
                      "px-2 py-0.5 text-xs rounded-full border",
                      timeLeft <= (gameSettings.timer.timerWarning || 10)
                        ? "bg-red-500/20 text-red-300 border-red-500/20 animate-pulse"
                        : "bg-primary/20 text-primary border-primary/20"
                    )}
                  >
                    {formatTime(timeLeft)}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge
              variant="default"
              className="relative px-3 py-1 text-xs "
            >
              Score: {playerScore}
            </Badge>
            {/*
            {playerCardCount > 0 && (
              <Badge
                variant="default"
                className="relative px-3 py-1 text-xs "
              >
                Cards: {playerCardCount}
              </Badge>
            )}
            */}
            {isPlayerTurn && cardsDealt && !isRoundEnded && (
              <div className="flex items-center gap-2">
                <Badge
                  variant="destructive"
                  className="relative px-3 py-1 text-xs "
                >
                  Turn
                </Badge>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};
